/**
 * "Open Draft" button for the meeting follow-up email tab.
 *
 * Opens the shared Compose Email dialog pre-filled with the AI-drafted
 * follow-up (subject, body, meeting attendees as recipients) so the user
 * can edit before sending from Coffee.ai — the draft is never sent as-is.
 * Sending, signature handling, and preview mode all live in the dialog.
 * Labeled "Open Draft" (not "Send Email") so users know nothing is sent
 * until they review the draft and hit Send inside the dialog.
 */
import { useState } from "react";
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import ComposeEmailDialog, { type Recipient } from "@/components/shared/ComposeEmailDialog";

interface Props {
  recipients: Recipient[];
  /** Internal attendees, pre-filled in Cc (defaults the dialog to one shared email). */
  ccRecipients?: Recipient[];
  subject: string;
  /** Follow-up email text without the signature (the dialog appends it). */
  body: string;
  disabled?: boolean;
  /** Saved-draft context (the meeting), so "Save Draft" is restored on the next Open Draft. */
  draftKey?: string;
  /** Called after "Save Draft" in the dialog, so the Email tab can show the saved body. */
  onSaveDraft?: (draft: { subject: string; body: string }) => void;
}

export default function SendFollowUpEmailButton({ recipients, ccRecipients, subject, body, disabled, draftKey, onSaveDraft }: Props) {
  const [composeOpen, setComposeOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <button
        onClick={() => setComposeOpen(true)}
        disabled={disabled}
        className="text-sm px-4 py-2 rounded-md bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <Pencil className="h-3.5 w-3.5" />
        {t("meetings.openDraft")}
      </button>

      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        recipients={recipients}
        initialCcRecipients={ccRecipients}
        initialSubject={subject}
        initialBody={body}
        draftKey={draftKey}
        onSaveDraft={onSaveDraft}
      />
    </>
  );
}
